/** Intraday / Positional switch for the Strategy Detail header
 * (docs/positional.md, phase E).
 *
 * INTRADAY strategies trade single contracts squared off daily;
 * POSITIONAL strategies run multi-leg option baskets held overnight
 * (paper-only until live enablement). Trades snapshot the horizon at
 * creation, so flipping only affects future entries — same contract as
 * the Live/Paper switch beside it. */

import { useState, type ReactElement } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { strategiesApi } from "@/lib/api";
import type { Horizon, Strategy } from "@/types/strategy";
import { STRATEGIES_KEY } from "./useStrategyActions";

const BLURB: Record<Horizon, string> = {
  POSITIONAL:
    "New trades become multi-leg option baskets held overnight (1-4 weeks): NRML product, exempt " +
    "from the daily square-off, marked to market at 15:25 and force-exited at expiry. " +
    "Paper-only for now. Any open intraday trade keeps running unchanged.",
  INTRADAY:
    "New trades go back to single-contract intraday behavior: MIS product, squared off by " +
    "15:24. Any open basket keeps running positionally until it closes.",
};

export function HorizonSwitch({ strategy }: Readonly<{ strategy: Strategy }>): ReactElement {
  const qc = useQueryClient();
  const [confirming, setConfirming] = useState(false);
  const mut = useMutation({
    mutationFn: (horizon: Horizon) => strategiesApi.update(strategy.id, { horizon }),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: STRATEGIES_KEY });
      setConfirming(false);
    },
  });

  const positional = strategy.horizon === "POSITIONAL";
  const next: Horizon = positional ? "INTRADAY" : "POSITIONAL";
  const nextLabel = positional ? "Intraday" : "Positional";
  return (
    <>
      <button
        type="button"
        onClick={() => {
          setConfirming(true);
        }}
        className={`pill hover:opacity-80 ${
          positional ? "bg-accent-bg text-accent" : "bg-surface-2 text-ink-secondary"
        }`}
        aria-label="Switch trade horizon"
      >
        <i className={`ti ${positional ? "ti-calendar-week" : "ti-sun-high"} text-sm`} />
        {positional ? "Positional" : "Intraday"}
        <i className="ti ti-switch-horizontal text-xs" />
      </button>
      <ConfirmDialog
        open={confirming}
        title={`Switch horizon to ${nextLabel}?`}
        description={BLURB[next]}
        confirmLabel={`Go ${nextLabel}`}
        icon={next === "POSITIONAL" ? "ti-calendar-week" : "ti-sun-high"}
        tone="warning"
        busy={mut.isPending}
        onCancel={() => {
          setConfirming(false);
        }}
        onConfirm={() => {
          mut.mutate(next);
        }}
      />
    </>
  );
}
